import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  FiSearch,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiLayers,
  FiCheckCircle,
  FiAlertCircle,
  FiInbox,
  FiAlertTriangle,
  FiRefreshCw,
  FiTag,
  FiPackage,
  FiToggleRight,
} from "react-icons/fi";
import AdminHeader from "../../Header/AdminHeader";
import AdminFooter from "../../Footer/AdminFooter";
import AdminLeftMenu from "../../Navbar/AdminLeftMenu";
import styles from "./UserPlan.module.css";
import { userloginDetails } from "../../Utils/AdminAuth";
import {
  fetchPlans,
  createUserPlan,
  updateUserPlan,
  deleteUserPlan
} from "./UserPlanApi";

const DURATION_OPTIONS = ["Monthly", "Quarterly", "Half-Yearly", "Yearly", "One-time"];
const PAGE_SIZE = 10;
const CURRENCY_SYMBOLS = {
  INR: "₹",
  USD: "$",
  EUR: "€",
};

const emptyFormData = {
  title: "",
  description: "",
  type: "main",
  planFor: "",
  amountInr: "",
  amountUsd: "",
  amountEur: "",
  duration: "",
  // features: [],
  status: "active",
  channel: "",
  humanAgentSeats: 0,
  aiChatAgent: 0,
  includedAiTokens: 0,
  whatsAppChatWidget: 0,
  authkeyBranding: 0,
};

const makeFeatureId = () => `f_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// Values pulled from the public pricing cards (Business / Scale tiers).
// Price fields are intentionally NOT included here — those stay user-editable.
const HARDCODED_PLAN_FEATURES = {
  business: {
    humanAgentSeats: 5,
    aiChatAgent: 1,
    includedAiTokens: 5, // millions/month
    whatsAppChatWidget: 1, // white-labelled widget included
    authkeyBranding: 0, // white-labelled = no Authkey branding
  },
  scale: {
    humanAgentSeats: 7,
    aiChatAgent: 1,
    includedAiTokens: 5, // millions/month
    whatsAppChatWidget: 1,
    authkeyBranding: 0,
  },
};

const getHardcodedFeatures = (title) =>
  HARDCODED_PLAN_FEATURES[(title || "").trim().toLowerCase()] || null;


const normalizePlan = (p) => ({
  id: p.id ?? p._id ?? p.plan_id ?? p.planId,
  title: p.plan_title ?? p.title ?? "",
  description: p.plan_description ?? p.description ?? "",
  type: p.plan_type ?? p.type ?? "main",
  planFor: p.plan_for ?? p.planFor ?? "",
  amountInr: p.amount_inr ?? p.amountInr ?? "",
  amountUsd: p.amount_usd ?? p.amountUsd ?? "",
  amountEur: p.amount_eur ?? p.amountEur ?? "",
  price: p.amount_inr ?? p.amountInr ?? 0,
  duration: p.plan_duration ?? p.duration ?? "",
  // features: p.features ?? [],
  status:
    Number(p.status) === 1
      ? "active"
      : Number(p.status) === 0
        ? "inactive"
        : (Number(p.is_active) === 0 ? "inactive" : "active"),
  createdAt: p.created_at ?? p.created ?? null,
  platformFeeWaiverAmount: p.platform_fee_waiver_amount ?? 0,
  platformFeeWaiverEnabled: p.platform_fee_waiver_enabled ?? 0,
  //sending multiple channelvalues as comma separated string, e.g. "whatsapp,instagram"
  channel: p.channel ?? null,
  humanAgentSeats: p.human_agent_seats ?? p.humanAgentSeats ?? 0,
  aiChatAgent: p.ai_chat_agent ?? p.aiChatAgent ?? 0,
  includedAiTokens: p.included_ai_tokens ?? p.includedAiTokens ?? 0,
  whatsAppChatWidget: p.whatsApp_chat_widget ?? p.whatsAppChatWidget ?? 0,
  authkeyBranding: p.authkey_branding ?? p.authkeyBranding ?? 0,
});

const UserPlanManagement = () => {

  const loginData = userloginDetails();
  const userId = loginData?.user?.userId || loginData?.userId;
  const token =
    loginData?.token ||
    loginData?.user?.token ||
    loginData?.user?.authToken ||
    loginData?.user?.accessToken;


  const [allPlans, setAllPlans] = useState([]);

  // Load / error state
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Filters
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  // Pagination
  const [page, setPage] = useState(1);

  // Feature preview (table)
  const [expandedFeaturesId, setExpandedFeaturesId] = useState(null);

  // Form modal
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create"); // 'create' | 'edit'
  const [activePlanId, setActivePlanId] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Toasts
  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef({});

  const hasActiveFilters =
    search !== "" || typeFilter !== "all" || statusFilter !== "all" || sortBy !== "newest";

  /* --------------------------- Toast helpers --------------------------- */

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (toastTimers.current[id]) {
      clearTimeout(toastTimers.current[id]);
      delete toastTimers.current[id];
    }
  }, []);

  const showToast = useCallback(
    (type, message) => {
      const id = `t_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      setToasts((prev) => [...prev, { id, type, message }]);
      toastTimers.current[id] = setTimeout(() => removeToast(id), 3800);
    },
    [removeToast]
  );

  useEffect(() => {
    const timers = toastTimers.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  /* ------------------------- Debounce search input ---------------------- */

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  /* ------------------------------ Data fetch ----------------------------- */

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await fetchPlans({ userId, token });
      const list = res?.data ?? res?.plans ?? [];
      setAllPlans(Array.isArray(list) ? list.map(normalizePlan) : []);
    } catch (err) {
      setLoadError(true);
      setAllPlans([]);
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  /* ------------------------ Curreny conversion----------------------- */

  const [exchangeRates, setExchangeRates] = useState({ usdPerInr: null, eurPerInr: null });
  const [ratesLoading, setRatesLoading] = useState(false);

  useEffect(() => {
    if (!isFormOpen) return;
    let cancelled = false;

    const fetchRates = async () => {
      setRatesLoading(true);
      try {
        // Free, no API key required
        const res = await fetch("https://open.er-api.com/v6/latest/INR");
        const data = await res.json();
        if (!cancelled && data?.rates) {
          setExchangeRates({
            usdPerInr: data.rates.USD,
            eurPerInr: data.rates.EUR,
          });
        }
      } catch (err) {
        // silently fall back to manual entry if the API fails
      } finally {
        if (!cancelled) setRatesLoading(false);
      }
    };

    fetchRates();
    return () => { cancelled = true; };
  }, [isFormOpen]);

  const handleAmountInrChange = (value) => {
    setFormData((prev) => {
      const next = { ...prev, amountInr: value };
      const inrNum = Number(value);
      if (exchangeRates.usdPerInr && exchangeRates.eurPerInr && value !== "" && !Number.isNaN(inrNum)) {
        next.amountUsd = (inrNum * exchangeRates.usdPerInr).toFixed(2);
        next.amountEur = (inrNum * exchangeRates.eurPerInr).toFixed(2);
      }
      return next;
    });
    setFormErrors((prev) => ({ ...prev, amountInr: undefined }));
  };

  /* ------------------------ Client-side filter/sort ----------------------- */

  const filteredPlans = useMemo(() => {
    let list = [...allPlans];

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          (p.title || "").toLowerCase().includes(q) ||
          (p.description || "").toLowerCase().includes(q)
      );
    }

    if (typeFilter !== "all") list = list.filter((p) => p.type === typeFilter);
    if (statusFilter !== "all") list = list.filter((p) => p.status === statusFilter);

    list.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        case "price_low":
          return (Number(a.price) || 0) - (Number(b.price) || 0);
        case "price_high":
          return (Number(b.price) || 0) - (Number(a.price) || 0);
        case "newest":
        default:
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
    });

    return list;
  }, [allPlans, search, typeFilter, statusFilter, sortBy]);

  const stats = useMemo(
    () => ({
      total: allPlans.length,
      main: allPlans.filter((p) => p.type === "main_plan").length,
      addon: allPlans.filter((p) => p.type === "add_on").length,
      active: allPlans.filter((p) => p.status === "active").length,
    }),
    [allPlans]
  );

  const total = filteredPlans.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Keep the current page in range if filtering shrinks the result set.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const plans = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredPlans.slice(start, start + PAGE_SIZE);
  }, [filteredPlans, page]);

  /* -------------------------------- Filters ------------------------------ */

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setTypeFilter("all");
    setStatusFilter("all");
    setSortBy("newest");
    setPage(1);
  };

  /* --------------------------------- Form --------------------------------- */

  const openCreateForm = () => {
    setFormMode("create");
    setActivePlanId(null);
    setFormData(emptyFormData);
    setFormErrors({});
    setIsFormOpen(true);
  };

  const openEditForm = (plan) => {
    setFormMode("edit");
    setActivePlanId(plan.id);
    // For Business / Scale plans, always show the hardcoded feature values
    //  instead of whatever is stored in the DB.
    const preset = getHardcodedFeatures(plan.title);
    setFormData({
      title: plan.title || "",
      description: plan.description || "",
      type: plan.type || "main_plan",
      planFor: plan.planFor || "",
      amountInr: plan.amountInr ?? "",
      amountUsd: plan.amountUsd ?? "",
      amountEur: plan.amountEur ?? "",
      duration: plan.duration || "",
      // features: (plan.features || []).map((f) =>
      //   typeof f === "string" ? { id: makeFeatureId(), value: f } : { id: f.id || makeFeatureId(), value: f.value ?? f }
      // ),
      status: plan.status || "active",
      introductoryOffer: plan.introductoryOffer ?? null,
      platformFeeWaiverEnabled: plan.platformFeeWaiverEnabled ?? 0,
      //sending multiple channelvalues as comma separated string, e.g. "whatsapp,instagram"
      channel: plan.channel ?? "",
      humanAgentSeats: preset ? preset.humanAgentSeats : (plan.humanAgentSeats ?? 0),
      aiChatAgent: preset ? preset.aiChatAgent : (plan.aiChatAgent ?? 0),
      includedAiTokens: preset ? preset.includedAiTokens : (plan.includedAiTokens ?? 0),
      whatsAppChatWidget: preset ? preset.whatsAppChatWidget : (plan.whatsAppChatWidget ?? 0),
      authkeyBranding: preset ? preset.authkeyBranding : (plan.authkeyBranding ?? 0),
      platformFeeWaiverAmount: plan.platformFeeWaiverAmount ?? 0
    });
    setFormErrors({});
    setIsFormOpen(true);
  };

  const closeForm = () => {
    if (submitting) return;
    setIsFormOpen(false);
  };

  const updateFormField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // As the user types a plan title, auto-fill the feature/limit fields when
  // the title matches a hardcoded tier (Business / Scale), and reset them
  // back to 0 for any other title so stale values don't linger.
  const handleTitleChange = (value) => {
    const preset = getHardcodedFeatures(value);
    setFormData((prev) => ({
      ...prev,
      title: value,
      ...(preset
        ? preset
        : {
          humanAgentSeats: 0,
          aiChatAgent: 0,
          includedAiTokens: 0,
          whatsAppChatWidget: 0,
          authkeyBranding: 0,
        }),
    }));
    setFormErrors((prev) => ({ ...prev, title: undefined }));
  };

  // const addFeatureField = () => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     features: [...prev.features, { id: makeFeatureId(), value: "" }],
  //   }));
  // };

  // const updateFeatureField = (id, value) => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     features: prev.features.map((f) => (f.id === id ? { ...f, value } : f)),
  //   }));
  // };

  // const removeFeatureField = (id) => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     features: prev.features.filter((f) => f.id !== id),
  //   }));
  // };

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) errors.title = "Plan title is required.";
    if (!formData.planFor.trim()) errors.planFor = "Plan For is required.";
    if (!formData.type) errors.type = "Please select a plan type.";

    const inrNum = Number(formData.amountInr);
    if (formData.amountInr === "" || Number.isNaN(inrNum) || inrNum < 0) {
      errors.amountInr = "Please enter a valid INR amount.";
    }
    if (formData.amountUsd !== "" && Number.isNaN(Number(formData.amountUsd))) {
      errors.amountUsd = "Please enter a valid USD amount.";
    }
    if (formData.amountEur !== "" && Number.isNaN(Number(formData.amountEur))) {
      errors.amountEur = "Please enter a valid EUR amount.";
    }
    if (!formData.duration) errors.duration = "Please select a duration.";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const payload = {
        userId,
        token,
        planTitle: formData.title.trim(),
        planFor: formData.planFor.trim(),
        planDuration: formData.duration,
        planDescription: formData.description.trim(),
        amountInr: Number(formData.amountInr),
        amountUsd: formData.amountUsd === "" ? undefined : Number(formData.amountUsd),
        amountEur: formData.amountEur === "" ? undefined : Number(formData.amountEur),
        planType: formData.type,
        status: formData.status,
        // features: formData.features.map((f) => f.value.trim()).filter((v) => v !== ""),
        introductoryOffer: formData.introductoryOffer,
        platformFeeWaiverEnabled: formData.platformFeeWaiverEnabled,
        //sending multiple channelvalues as comma separated string, e.g. "whatsapp,instagram"
        channel: formData.channel || null,
        humanAgentSeats: formData.humanAgentSeats,
        aiChatAgent: formData.aiChatAgent,
        includedAiTokens: formData.includedAiTokens,
        whatsAppChatWidget: formData.whatsAppChatWidget,
        authkeyBranding: formData.authkeyBranding,
        platformFeeWaiverAmount: formData.platformFeeWaiverAmount,
      };

      if (formMode === "edit") {
        await updateUserPlan({ ...payload, planId: activePlanId });
        showToast("success", "Plan updated successfully.");
      } else {
        await createUserPlan(payload);
        showToast("success", "Plan created successfully.");
      }

      setIsFormOpen(false);
      loadPlans();
    } catch (err) {
      showToast(
        "error",
        err?.message || `Failed to ${formMode === "edit" ? "update" : "create"} plan.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* -------------------------------- Delete -------------------------------- */



  const openDeleteModal = (plan) => setDeleteTarget(plan);
  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      await deleteUserPlan({
        userId,
        token,
        planId: deleteTarget.id,
      });
      showToast("success", "Plan deleted successfully.");
      setDeleteTarget(null);
      loadPlans();
    } catch (err) {
      showToast("error", err?.message || "Failed to delete plan.");
    } finally {
      setDeleting(false);
    }
  };

  /* ------------------------------ Pagination ------------------------------ */

  const getPageNumbers = () => {
    const pages = [];
    const windowSize = 1;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= page - windowSize && i <= page + windowSize)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== "...") {
        pages.push("...");
      }
    }
    return pages;
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  /* --------------------------------------------------------------------- */
  /* Render helpers                                                        */
  /* --------------------------------------------------------------------- */

  const renderTypeBadge = (type) =>
    type === "add_on" ? (
      <span className={`${styles.badge} ${styles.badgeAddon}`}>Add-on</span>
    ) : (
      <span className={`${styles.badge} ${styles.badgeMain}`}>Main</span>
    );

  const renderStatusBadge = (status) =>
    status === "active" ? (
      <span className={`${styles.badge} ${styles.badgeActive}`}>Active</span>
    ) : (
      <span className={`${styles.badge} ${styles.badgeInactive}`}>Inactive</span>
    );

  const formatPrice = (price) => {
    const num = Number(price);
    if (Number.isNaN(num)) return price || "\u2014";
    return `${CURRENCY_SYMBOLS.INR}${num.toLocaleString("en-IN")}`;
  };

  const formatSecondaryPrice = (plan) => {
    const parts = [];
    if (plan.amountUsd !== "" && plan.amountUsd !== undefined && plan.amountUsd !== null) {
      parts.push(`${CURRENCY_SYMBOLS.USD}${Number(plan.amountUsd).toLocaleString("en-US")}`);
    }
    if (plan.amountEur !== "" && plan.amountEur !== undefined && plan.amountEur !== null) {
      parts.push(`${CURRENCY_SYMBOLS.EUR}${Number(plan.amountEur).toLocaleString("en-US")}`);
    }
    return parts.join(" \u00B7 ");
  };

  const formatDate = (value) => {
    if (!value) return "\u2014";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  /* --------------------------------------------------------------------- */
  /* Render                                                                 */
  /* --------------------------------------------------------------------- */

  return (
    <div className="wrapper">
      <AdminLeftMenu />

      <div className="main">
        <AdminHeader />

        <main className="content">
          <div className="container-fluid p-0">
            <div className={styles.wrapper}>
              {/* Toasts */}
              <div className={styles.toastContainer} aria-live="polite">
                {toasts.map((t) => (
                  <div
                    key={t.id}
                    className={`${styles.toast} ${t.type === "success" ? styles.toastSuccess : styles.toastError}`}
                    role="status"
                  >
                    <span className={styles.toastIcon}>
                      {t.type === "success" ? <FiCheckCircle /> : <FiAlertCircle />}
                    </span>
                    <span>{t.message}</span>
                    <button
                      type="button"
                      className={styles.toastClose}
                      onClick={() => removeToast(t.id)}
                      aria-label="Dismiss notification"
                    >
                      <FiX />
                    </button>
                  </div>
                ))}
              </div>

              {/* Page header */}
              <div className={styles.pageHeader}>
                <div>
                  <h1 className={styles.pageTitle}>User Plan Management</h1>
                  <p className={styles.pageDescription}>Create and manage the plans available to users.</p>
                </div>
                <button type="button" className={styles.primaryButton} onClick={openCreateForm}>
                  <FiPlus /> Create New Plan
                </button>
              </div>

              {/* Stats */}
              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <span className={styles.statIcon}>
                    <FiLayers />
                  </span>
                  <div>
                    <p className={styles.statLabel}>Total Plans</p>
                    <p className={styles.statValue}>{stats.total}</p>
                  </div>
                </div>
                <div className={styles.statCard}>
                  <span className={styles.statIcon}>
                    <FiPackage />
                  </span>
                  <div>
                    <p className={styles.statLabel}>Main Plans</p>
                    <p className={styles.statValue}>{stats.main}</p>
                  </div>
                </div>
                <div className={styles.statCard}>
                  <span className={styles.statIcon}>
                    <FiTag />
                  </span>
                  <div>
                    <p className={styles.statLabel}>Add-on Plans</p>
                    <p className={styles.statValue}>{stats.addon}</p>
                  </div>
                </div>
                <div className={styles.statCard}>
                  <span className={styles.statIcon}>
                    <FiToggleRight />
                  </span>
                  <div>
                    <p className={styles.statLabel}>Active Plans</p>
                    <p className={styles.statValue}>{stats.active}</p>
                  </div>
                </div>
              </div>

              {/* Toolbar */}
              <div className={styles.toolbar}>
                <div className={styles.searchBox}>
                  <FiSearch />
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder="Search plans..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    aria-label="Search plans"
                  />
                </div>

                <select
                  className={styles.selectInput}
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setPage(1);
                  }}
                  aria-label="Filter by plan type"
                >
                  <option value="all">All Types</option>
                  <option value="main_plan">Main Plan</option>
                  <option value="add_on">Add-on Plan</option>
                </select>

                <select
                  className={styles.selectInput}
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  aria-label="Filter by status"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>

                <select
                  className={styles.selectInput}
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                  aria-label="Sort plans"
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="price_low">Price: Low to High</option>
                  <option value="price_high">Price: High to Low</option>
                </select>

                {hasActiveFilters && (
                  <button type="button" className={styles.clearFiltersButton} onClick={clearFilters}>
                    <FiX /> Clear Filters
                  </button>
                )}
              </div>

              {/* Table */}
              <div className={styles.tableCard}>
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Plan</th>
                        <th>Type</th>
                        <th>For</th>
                        <th>Price</th>
                        <th>Duration</th>
                        {/* <th>Features</th> */}
                        <th>Status</th>
                        <th>Created</th>
                        <th>Channel</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading &&
                        Array.from({ length: 6 }).map((_, i) => (
                          <tr key={`sk_${i}`}>
                            {Array.from({ length: 9 }).map((__, j) => (
                              <td key={j}>
                                <div className={styles.skeletonCell} style={{ width: j === 0 ? "70%" : "50%" }} />
                              </td>
                            ))}
                          </tr>
                        ))}

                      {!loading && loadError && (
                        <tr>
                          <td colSpan={9}>
                            <div className={styles.stateBox}>
                              <span className={styles.stateIcon}>
                                <FiAlertTriangle />
                              </span>
                              <p className={styles.stateTitle}>Unable to load plans.</p>
                              <button type="button" className={styles.secondaryButton} onClick={loadPlans}>
                                <FiRefreshCw /> Retry
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}

                      {!loading && !loadError && plans.length === 0 && !hasActiveFilters && (
                        <tr>
                          <td colSpan={9}>
                            <div className={styles.stateBox}>
                              <span className={styles.stateIcon}>
                                <FiInbox />
                              </span>
                              <p className={styles.stateTitle}>No plans created yet</p>
                              <p className={styles.stateSubtitle}>
                                Create your first plan to make it available to users.
                              </p>
                              <button type="button" className={styles.primaryButton} onClick={openCreateForm}>
                                <FiPlus /> Create New Plan
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}

                      {!loading && !loadError && plans.length === 0 && hasActiveFilters && (
                        <tr>
                          <td colSpan={9}>
                            <div className={styles.stateBox}>
                              <span className={styles.stateIcon}>
                                <FiSearch />
                              </span>
                              <p className={styles.stateTitle}>No plans match your current filters.</p>
                              <button type="button" className={styles.secondaryButton} onClick={clearFilters}>
                                Clear Filters
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}

                      {!loading &&
                        !loadError &&
                        plans.map((plan) => (
                          <tr key={plan.id}>
                            <td>
                              <p className={styles.cellPrimary}>{plan.title}</p>
                              {/* truncate the description to 30 characters and add ellipsis if longer */}
                              {plan.description && (
                                <p className={styles.cellSecondary}>
                                  {plan.description.length > 30 ? `${plan.description.substring(0, 30)}....` : plan.description}
                                </p>
                              )}
                            </td>
                            <td>{renderTypeBadge(plan.type)}</td>
                            <td>{plan.planFor || "\u2014"}</td>
                            <td>
                              {formatPrice(plan.amountInr)}
                              {formatSecondaryPrice(plan) && (
                                <p className={styles.cellSecondary}>{formatSecondaryPrice(plan)}</p>
                              )}
                            </td>
                            <td>{plan.duration}</td>
                            {/* <td style={{ position: "relative" }}>
                              <button
                                type="button"
                                className={styles.featuresPill}
                                onClick={() =>
                                  setExpandedFeaturesId((prev) => (prev === plan.id ? null : plan.id))
                                }
                              >
                                {(plan.features || []).length} Features
                              </button>
                              {expandedFeaturesId === plan.id && (plan.features || []).length > 0 && (
                                <div className={styles.featuresPreview}>
                                  <ul>
                                    {plan.features.map((f, idx) => (
                                      <li key={idx}>{typeof f === "string" ? f : f.value}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </td> */}
                            <td>{renderStatusBadge(plan.status)}</td>
                            <td>{formatDate(plan.createdAt)}</td>
                            <td>{plan.channel || "\u2014"}</td>
                            <td>
                              <div className={styles.rowActions}>
                                <button
                                  type="button"
                                  className={styles.iconButton}
                                  onClick={() => openEditForm(plan)}
                                  aria-label={`Edit ${plan.title}`}
                                >
                                  <FiEdit2 />
                                  <span className={styles.tooltip}>Edit</span>
                                </button>
                                <button
                                  type="button"
                                  className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                                  onClick={() => openDeleteModal(plan)}
                                  aria-label={`Delete ${plan.title}`}
                                >
                                  <FiTrash2 />
                                  <span className={styles.tooltip}>Delete</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {!loading && !loadError && total > 0 && (
                  <div className={styles.paginationBar}>
                    <span className={styles.paginationInfo}>
                      Showing {rangeStart} to {rangeEnd} of {total} plans
                    </span>
                    <div className={styles.paginationControls}>
                      <button
                        type="button"
                        className={styles.pageButton}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        aria-label="Previous page"
                      >
                        <FiChevronLeft />
                      </button>
                      {getPageNumbers().map((p, idx) =>
                        p === "..." ? (
                          <span key={`e_${idx}`} className={styles.pageEllipsis}>
                            ...
                          </span>
                        ) : (
                          <button
                            key={p}
                            type="button"
                            className={`${styles.pageButton} ${p === page ? styles.pageButtonActive : ""}`}
                            onClick={() => setPage(p)}
                            aria-current={p === page ? "page" : undefined}
                          >
                            {p}
                          </button>
                        )
                      )}
                      <button
                        type="button"
                        className={styles.pageButton}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        aria-label="Next page"
                      >
                        <FiChevronRight />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Create / Edit modal */}
              {isFormOpen && (
                <div className={styles.modalOverlay} onMouseDown={(e) => e.target === e.currentTarget && closeForm()}>
                  <div className={styles.modalCard} role="dialog" aria-modal="true" aria-labelledby="planFormTitle">
                    <form onSubmit={handleSubmitForm}>
                      <div className={styles.modalHeader}>
                        <h2 className={styles.modalTitle} id="planFormTitle">
                          {formMode === "create" ? "Create New Plan" : "Edit Plan"}
                        </h2>
                        <button
                          type="button"
                          className={styles.modalCloseButton}
                          onClick={closeForm}
                          aria-label="Close"
                        >
                          <FiX />
                        </button>
                      </div>

                      <div className={styles.modalBody}>
                        {formMode === "edit" && (
                          //connecting to updateuserplan endpoint for edit mode
                          <div className={styles.formSection}>
                            <p className={styles.formSectionTitle}>Plan ID</p>
                            <p className={styles.formSectionSubtitle}>{activePlanId}</p>
                          </div>

                        )}

                        {/* Basic information */}
                        <div className={styles.formSection}>
                          <p className={styles.formSectionTitle}>Basic Information</p>

                          <div className={styles.formGroup}>
                            <label className={styles.formLabel} htmlFor="planTitle">
                              Plan Title
                            </label>
                            <input
                              id="planTitle"
                              type="text"
                              className={`${styles.formInput} ${formErrors.title ? styles.formInputError : ""}`}
                              placeholder="Enter plan title"
                              value={formData.title}
                              onChange={(e) => handleTitleChange(e.target.value)}
                            />
                            {formErrors.title && <p className={styles.formError}>{formErrors.title}</p>}
                          </div>

                          <div className={styles.formGroup}>
                            <label className={styles.formLabel} htmlFor="planFor">
                              Plan For
                            </label>
                            {/* add dropdown for planFor with options monthly_cost, widget, other */}
                            <select
                              id="planFor"
                              className={`${styles.formInput} ${formErrors.planFor ? styles.formInputError : ""}`}
                              value={formData.planFor}
                              onChange={(e) => updateFormField("planFor", e.target.value)}
                            >
                              <option value="">Select an option</option>
                              <option value="monthly_cost">Monthly Cost</option>
                              <option value="widget">Widget</option>
                              <option value="other">Other</option>
                            </select>
                            {formErrors.planFor && <p className={styles.formError}>{formErrors.planFor}</p>}
                          </div>

                          <div className={styles.formGroup}>
                            <label className={styles.formLabel} htmlFor="planDescription">
                              Description
                            </label>
                            <textarea
                              id="planDescription"
                              className={styles.formTextarea}
                              placeholder="Describe what this plan includes..."
                              value={formData.description}
                              onChange={(e) => updateFormField("description", e.target.value)}
                            />
                          </div>
                          {/* sending multiple channelvalues as comma separated string, e.g. "whatsapp,instagram" , so add multiple options selection

                          <div className={styles.formGroup}>
                            <label className={styles.formLabel} htmlFor="planChannel">
                              Channel
                            </label>
                            <select
                              id="planChannel"
                              className={`${styles.formInput} ${formErrors.channel ? styles.formInputError : ""}`}
                              value={formData.channel}
                              onChange={(e) => updateFormField("channel", e.target.value)}
                            >
                              <option value="">Select an option</option>
                              <option value="wp">WhatsApp</option>
                              <option value="sms">SMS</option>
                              <option value="voice">Voice</option>
                              <option value="email">Email</option>
                              <option value="rcs">RCS</option>
                            </select>
                            {formErrors.channel && <p className={styles.formError}>{formErrors.channel}</p>}
                          </div> */}

                          {/* sending multiple channel values as comma separated string, e.g. "whatsapp,instagram" */}
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Channel</label>
                            <div className={styles.checkboxGroup}>
                              {[
                                { value: "wp", label: "WhatsApp" },
                                { value: "sms", label: "SMS" },
                                { value: "voice", label: "Voice" },
                                { value: "email", label: "Email" },
                                { value: "rcs", label: "RCS" },
                              ].map((option) => {
                                const selectedValues = formData.channel ? formData.channel.split(",") : [];
                                const isChecked = selectedValues.includes(option.value);

                                return (
                                  <label key={option.value} className={styles.checkboxLabel}>
                                    <input
                                      type="checkbox"
                                      value={option.value}
                                      checked={isChecked}
                                      onChange={(e) => {
                                        const current = formData.channel ? formData.channel.split(",") : [];
                                        const updated = e.target.checked
                                          ? [...current, option.value]
                                          : current.filter((v) => v !== option.value);
                                        updateFormField("channel", updated.join(","));
                                      }}
                                    />
                                    {option.label}
                                  </label>
                                );
                              })}
                            </div>
                            {formErrors.channel && <p className={styles.formError}>{formErrors.channel}</p>}
                          </div>

                          {/* yes or no switch for platform fee waiver enabled */}
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel} htmlFor="platformFeeWaiverEnabled">
                              Platform Fee Waiver Enabled
                            </label>
                            <div className={styles.formCheckbox}>
                              <input
                                id="platformFeeWaiverEnabled"
                                type="checkbox"
                                checked={formData.platformFeeWaiverEnabled}
                                onChange={(e) => updateFormField("platformFeeWaiverEnabled", e.target.checked)}
                              />
                              <label htmlFor="platformFeeWaiverEnabled" style={{ fontSize: 14, cursor: "pointer" }}>
                                Enable waiver
                              </label>
                            </div>
                          </div>

                          {formData.platformFeeWaiverEnabled && (
                            <div className={styles.formGroup}>
                              <label className={styles.formLabel} htmlFor="platformFeeWaiverAmount">
                                Platform Fee Waiver Amount
                              </label>
                              <input
                                id="platformFeeWaiverAmount"
                                type="number"
                                className={`${styles.formInput} ${formErrors.platformFeeWaiverAmount ? styles.formInputError : ""}`}
                                placeholder="Enter platform fee waiver amount"
                                value={formData.platformFeeWaiverAmount}
                                onChange={(e) => updateFormField("platformFeeWaiverAmount", e.target.value)}
                              />
                              {formErrors.platformFeeWaiverAmount && <p className={styles.formError}>{formErrors.platformFeeWaiverAmount}</p>}
                            </div>
                          )}

                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Plan Type</label>
                            <div className={styles.radioCardGroup} role="radiogroup" aria-label="Plan type">
                              <label
                                className={`${styles.radioCard} ${formData.type === "main_plan" ? styles.radioCardSelected : ""}`}
                              >
                                <input
                                  type="radio"
                                  name="planType"
                                  value="main_plan"
                                  checked={formData.type === "main_plan"}
                                  onChange={() => updateFormField("type", "main_plan")}
                                />
                                Main Plan
                              </label>
                              <label
                                className={`${styles.radioCard} ${formData.type === "add_on" ? styles.radioCardSelected : ""}`}
                              >
                                <input
                                  type="radio"
                                  name="planType"
                                  value="add_on"
                                  checked={formData.type === "add_on"}
                                  onChange={() => updateFormField("type", "add_on")}
                                />
                                Add-on Plan
                              </label>
                            </div>
                            {formErrors.type && <p className={styles.formError}>{formErrors.type}</p>}
                          </div>
                        </div>

                        {/* Pricing */}
                        <div className={styles.formSection}>
                          <p className={styles.formSectionTitle}>Pricing</p>
                          <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                              <label className={styles.formLabel} htmlFor="planAmountInr">
                                Amount (INR)
                              </label>
                              <div className={styles.priceInputWrap}>
                                <span className={styles.currencyPrefix}>{CURRENCY_SYMBOLS.INR}</span>
                                <input
                                  id="planAmountInr"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder="999"
                                  value={formData.amountInr}
                                  onChange={(e) => handleAmountInrChange(e.target.value)}   
                                />
                              </div>
                              {formErrors.amountInr && <p className={styles.formError}>{formErrors.amountInr}</p>}
                            </div>

                            <div className={styles.formGroup}>
                              <label className={styles.formLabel} htmlFor="planAmountUsd">
                                Amount (USD)
                              </label>
                              <div className={styles.priceInputWrap}>
                                <span className={styles.currencyPrefix}>{CURRENCY_SYMBOLS.USD}</span>
                                <input
                                  id="planAmountUsd"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder="12"
                                  value={formData.amountUsd}
                                  onChange={(e) => updateFormField("amountUsd", e.target.value)}
                                />
                              </div>
                              {formErrors.amountUsd && <p className={styles.formError}>{formErrors.amountUsd}</p>}
                            </div>

                            <div className={styles.formGroup}>
                              <label className={styles.formLabel} htmlFor="planAmountEur">
                                Amount (EUR)
                              </label>
                              <div className={styles.priceInputWrap}>
                                <span className={styles.currencyPrefix}>{CURRENCY_SYMBOLS.EUR}</span>
                                <input
                                  id="planAmountEur"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder="11"
                                  value={formData.amountEur}
                                  onChange={(e) => updateFormField("amountEur", e.target.value)}
                                />
                              </div>
                              {formErrors.amountEur && <p className={styles.formError}>{formErrors.amountEur}</p>}
                            </div>
                          </div>

                          <div className={styles.formGroup}>
                            <label className={styles.formLabel} htmlFor="planDuration">
                              Duration
                            </label>
                            <select
                              id="planDuration"
                              className={`${styles.formSelect} ${formErrors.duration ? styles.formInputError : ""}`}
                              value={formData.duration}
                              onChange={(e) => updateFormField("duration", e.target.value)}
                            >
                              <option value="">Select duration</option>
                              {DURATION_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                            {formErrors.duration && <p className={styles.formError}>{formErrors.duration}</p>}
                          </div>
                        </div>

                        {/* Features */}
                        {/* <div className={styles.formSection}>
                          <p className={styles.formSectionTitle}>Features</p>
                          {formData.features.map((feature) => (
                            <div className={styles.featureRow} key={feature.id}>
                              <input
                                type="text"
                                className={styles.formInput}
                                placeholder="e.g. 10,000 API calls"
                                value={feature.value}
                                onChange={(e) => updateFeatureField(feature.id, e.target.value)}
                              />
                              <button
                                type="button"
                                className={styles.removeFeatureButton}
                                onClick={() => removeFeatureField(feature.id)}
                                aria-label="Remove feature"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          ))}
                          <button type="button" className={styles.addFeatureButton} onClick={addFeatureField}>
                            <FiPlus /> Add Feature
                          </button>
                        </div> */}

                        {/* Feature Limits — only shown for Business / Scale. Values default
                            from the pricing cards when the title is typed/matched, but are
                            read-only here so an admin can't accidentally edit them. */}
                        {getHardcodedFeatures(formData.title) && (
                          <div className={styles.formSection}>
                            <p className={styles.formSectionTitle}>Plan Features & Limits</p>
                            <p className={styles.formSectionSubtitle} style={{ marginBottom: 10, fontSize: 12 }}>
                              Auto-set based on the {formData.title} tier — not editable here.
                            </p>

                            <div className={styles.formRow}>
                              <div className={styles.formGroup}>
                                <label className={styles.formLabel} htmlFor="humanAgentSeats">
                                  Human Agent Seats
                                </label>
                                <input
                                  id="humanAgentSeats"
                                  type="number"
                                  min="0"
                                  className={styles.formInput}
                                  value={formData.humanAgentSeats}
                                  disabled
                                  readOnly
                                />
                              </div>

                              <div className={styles.formGroup}>
                                <label className={styles.formLabel} htmlFor="includedAiTokens">
                                  Included AI Tokens (millions/mo)
                                </label>
                                <input
                                  id="includedAiTokens"
                                  type="number"
                                  min="0"
                                  className={styles.formInput}
                                  value={formData.includedAiTokens}
                                  disabled
                                  readOnly
                                />
                              </div>
                            </div>

                            <div className={styles.formGroup}>
                              <div className={styles.formCheckbox}>
                                <input
                                  id="aiChatAgent"
                                  type="checkbox"
                                  checked={!!formData.aiChatAgent}
                                  disabled
                                  readOnly
                                />
                                <label htmlFor="aiChatAgent" style={{ fontSize: 14 }}>
                                  AI Chat Agent Included
                                </label>
                              </div>
                            </div>

                            <div className={styles.formGroup}>
                              <div className={styles.formCheckbox}>
                                <input
                                  id="whatsAppChatWidget"
                                  type="checkbox"
                                  checked={!!formData.whatsAppChatWidget}
                                  disabled
                                  readOnly
                                />
                                <label htmlFor="whatsAppChatWidget" style={{ fontSize: 14 }}>
                                  {/* White-labelled WhatsApp Chat Widget */}
                                  WhatsApp Chat Widget
                                </label>
                              </div>
                            </div>

                            {/* <div className={styles.formGroup}>
                              <div className={styles.formCheckbox}>
                                <input
                                  id="advancedcustomerengagement"
                                  type="checkbox"
                                  checked={!!formData.advancedcustomerengagement}
                                  onChange={(e) => updateFormField("advancedcustomerengagement", e.target.checked ? 1 : 0)}
                                />
                                <label htmlFor="advancedcustomerengagement" style={{ fontSize: 14, cursor: "pointer" }}>
                                  Advanced customer engagement
                                </label>
                              </div>
                            </div> */}

                            <div className={styles.formGroup}>
                              <div className={styles.formCheckbox}>
                                <input
                                  id="authkeyBranding"
                                  type="checkbox"
                                  checked={!!formData.authkeyBranding}
                                  disabled
                                  readOnly
                                />
                                <label htmlFor="authkeyBranding" style={{ fontSize: 14 }}>
                                  Show "Authkey" Branding
                                </label>
                              </div>
                              <p className={styles.formSectionSubtitle} style={{ marginTop: 4, fontSize: 12 }}>
                                White-labelled tiers ship with this off.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Status */}
                        <div className={styles.formSection} style={{ marginBottom: 0 }}>
                          <p className={styles.formSectionTitle}>Plan Status</p>
                          <div className={styles.statusToggleRow}>
                            <label className={styles.switch}>
                              <input
                                type="checkbox"
                                checked={formData.status === "active"}
                                onChange={(e) => updateFormField("status", e.target.checked ? "active" : "inactive")}
                              />
                              <span className={styles.switchTrack} />
                            </label>
                            {renderStatusBadge(formData.status)}
                          </div>
                        </div>
                      </div>

                      <div className={styles.modalFooter}>
                        <button type="button" className={styles.secondaryButton} onClick={closeForm} disabled={submitting}>
                          Cancel
                        </button>
                        <button type="submit" className={styles.primaryButton} disabled={submitting}>
                          {submitting ? (
                            <>
                              <span className={styles.spinner} /> {formMode === "create" ? "Creating..." : "Saving..."}
                            </>
                          ) : formMode === "create" ? (
                            "Create Plan"
                          ) : (
                            "Save Changes"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Delete confirmation modal */}
              {deleteTarget && (
                <div className={styles.modalOverlay} onMouseDown={(e) => e.target === e.currentTarget && closeDeleteModal()}>
                  <div
                    className={`${styles.modalCard} ${styles.modalCardSmall}`}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="deletePlanTitle"
                  >
                    <div className={styles.modalHeader}>
                      <h2 className={styles.modalTitle} id="deletePlanTitle">
                        Delete Plan?
                      </h2>
                      <button
                        type="button"
                        className={styles.modalCloseButton}
                        onClick={closeDeleteModal}
                        aria-label="Close"
                      >
                        <FiX />
                      </button>
                    </div>
                    <div className={styles.modalBody}>
                      <p className={styles.modalText}>
                        Are you sure you want to delete "{deleteTarget.title}"? This action cannot be undone.
                      </p>
                    </div>
                    <div className={styles.modalFooter}>
                      <button type="button" className={styles.secondaryButton} onClick={closeDeleteModal} disabled={deleting}>
                        Cancel
                      </button>
                      <button type="button" className={styles.dangerButton} onClick={handleConfirmDelete} disabled={deleting}>
                        {deleting ? (
                          <>
                            <span className={styles.spinner} /> Deleting...
                          </>
                        ) : (
                          "Delete Plan"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <AdminFooter />
      </div>
    </div>
  );
};

export default UserPlanManagement;